/**
 * financeData.js
 *
 * Derives finance/earnings data from existing bookings + organizationMembers.
 * No new Firestore collections — everything computed from bookings + member rates.
 *
 * Collective commission model:
 *   1. Deduct consumption tax: price ÷ 11 (tax-inclusive)
 *   2. Deduct card fee: 3.6% of full price (if card payment)
 *   3. Apply commission: Stage 1 = 25%, Stage 2 = 20%, Stage 3 = 18%
 *   4. Instructor earns remainder
 *   5. WPI deduction: 5% of gross monthly earnings
 *   6. Passed work: originating instructor earns 10% of lesson price
 *
 * School model: hourly (non-teach ¥1,000/hr, teach rate varies by level)
 */

import { resolveInstructorCategory, CATEGORY, resolveCurrentTeamState, CURRENT_SEASON_ID } from './instructorCategory';
import { isPositivelyUnpaid } from './paymentSemantics';

const MONTHS = ['December', 'January', 'February', 'March', 'April'];
// Reserved for future month-aware date filtering
// const MONTH_INDICES = { December: 11, January: 0, February: 1, March: 2, April: 3 };
// const MONTH_YEARS = { December: 2025, January: 2026, February: 2026, March: 2026, April: 2026 };

function toDate(val) {
  if (!val) return null;
  if (val.toDate) return val.toDate();
  if (val.seconds) return new Date(val.seconds * 1000);
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

function getMonthKey(date) {
  const m = date.getMonth();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return monthNames[m];
}

function fmt(n) {
  if (!n || n === 0) return '—';
  return '¥' + Math.round(n).toLocaleString();
}

// ── Collective commission calculation ───────────────────────────────────────

export function calcCollectiveEarnings(lessonPrice, { stage = 1, isCard = true } = {}) {
  const commissionRates = { 1: 0.25, 2: 0.20, 3: 0.18 };
  const commRate = commissionRates[stage] || 0.25;

  const tax = Math.round(lessonPrice / 11);
  const cardFee = isCard ? Math.round(lessonPrice * 0.036) : 0;
  const adjusted = lessonPrice - tax - cardFee;
  const commission = Math.round(adjusted * commRate);
  const instructorEarns = adjusted - commission;
  const pct = lessonPrice > 0 ? Math.round(instructorEarns / lessonPrice * 1000) / 10 : 0;
  const passedWork = Math.round(lessonPrice * 0.10);

  return { lessonPrice, tax, cardFee, adjusted, commission, commRate, instructorEarns, pct, passedWork };
}

// ── Build per-instructor season data ────────────────────────────────────────

/**
 * Derive finance data for all instructors from bookings + members.
 *
 * @param {Array} bookings - normalized booking objects
 * @param {Array} members - organizationMembers docs
 * @returns {{ instructors, seasonStats }}
 */
export function buildFinanceData(bookings, members) {
  // Pass 46H: the CURRENT team excludes historical-only people (currentTeam===false).
  // Historical membership ("worked with SAS in a past season") ≠ current active team.
  // They remain fully visible in historical season views, just not the current roster.
  const instructorMembers = members.filter(m => (m.role === 'instructor' || m.orgRole === 'instructor') && m.currentTeam !== false);
  const memberMap = {};
  for (const m of instructorMembers) memberMap[m.userId] = m;

  // Group bookings by instructor
  const byInstructor = {};
  for (const b of bookings) {
    if (b.status === 'canceled') continue;
    const instId = b.instructorId || '__unassigned__';
    if (!byInstructor[instId]) byInstructor[instId] = [];
    byInstructor[instId].push(b);
  }

  const instructors = [];

  for (const mem of instructorMembers) {
    const instBookings = byInstructor[mem.userId] || [];
    // Pass 46E: resolve category canonically (reads instructorPool OR instructorCategory OR
    // latest season affiliation). Missing → 'unclassified', NEVER a silent School default.
    const category = resolveInstructorCategory(mem);
    const pool = category === CATEGORY.COLLECTIVE ? 'collective' : category === CATEGORY.SCHOOL ? 'internal' : 'unclassified';
    const isCollective = category === CATEGORY.COLLECTIVE;
    const isUnclassified = category === CATEGORY.UNCLASSIFIED;
    // Pass 47: current-season (26/27) team state + season-specific category.
    const teamState = resolveCurrentTeamState(mem);
    const currentSeasonCategory = resolveInstructorCategory(mem, { season: CURRENT_SEASON_ID });
    const rate = isCollective ? (mem.labels?.dayRate || 0) : (mem.labels?.teachingRate || 0);

    // Group by month
    const months = {};
    for (const monthName of MONTHS) {
      months[monthName] = { bookings: [], fd: 0, hd: 0, hours: 0, grossEarn: 0, lessons: 0 };
    }

    for (const b of instBookings) {
      const start = toDate(b.startAt);
      if (!start) continue;
      const monthKey = getMonthKey(start);
      if (!months[monthKey]) continue;

      const dur = b.serviceDurationSnapshot || 420;
      const hours = dur / 60;
      const isFD = dur >= 360;
      const isHD = !isFD;

      let dailyEarn = 0;
      if (isCollective) {
        dailyEarn = isFD ? (mem.labels?.dayRate || 0) : Math.round((mem.labels?.dayRate || 0) * 0.6);
      } else {
        dailyEarn = hours * rate;
      }

      months[monthKey].bookings.push({
        date: start,
        dateStr: start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
        client: b.requesterName || 'Client',
        fd: isFD ? 1 : 0,
        hd: isHD ? 1 : 0,
        hours,
        earn: dailyEarn,
        price: b.priceSnapshot || 0,
        startTime: start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        endTime: '', // Not stored separately — derive from start + duration
        bookingId: b.id,
        paymentStatus: b.paymentStatus || b.paymentState || 'not_recorded', // Pass 47C: never default to 'unpaid'
      });

      months[monthKey].fd += isFD ? 1 : 0;
      months[monthKey].hd += isHD ? 1 : 0;
      months[monthKey].hours += hours;
      months[monthKey].grossEarn += dailyEarn;
      months[monthKey].lessons++;
    }

    // Compute season totals
    let seasonEarn = 0;
    let seasonFD = 0;
    let seasonHD = 0;
    let seasonHours = 0;
    let seasonLessons = 0;

    for (const monthName of MONTHS) {
      const mo = months[monthName];
      seasonEarn += mo.grossEarn;
      seasonFD += mo.fd;
      seasonHD += mo.hd;
      seasonHours += mo.hours;
      seasonLessons += mo.lessons;

      // Sort bookings by date
      mo.bookings.sort((a, b) => a.date - b.date);
    }

    instructors.push({
      userId: mem.userId,
      name: mem.displayName || mem.name || mem.userId,
      pool,
      category,
      isCollective,
      isUnclassified,
      teamState,
      currentSeasonCategory,
      lastKnownCategory: category,
      discipline: mem.labels?.discipline || '',
      rate,
      // TODO: Commission stage not stored on member docs yet — default to Stage 1
      // In production this would come from a contract/agreement field
      stage: 1,
      months,
      seasonEarn,
      seasonFD,
      seasonHD,
      seasonHours,
      seasonLessons,
    });
  }

  // Sort: collective first (higher earners first), then internal
  instructors.sort((a, b) => {
    if (a.pool !== b.pool) return a.isCollective ? -1 : 1;
    return b.seasonEarn - a.seasonEarn;
  });

  // Season-wide stats
  const colInstructors = instructors.filter(i => i.isCollective);
  const schInstructors = instructors.filter(i => !i.isCollective);
  const colSeasonEarn = colInstructors.reduce((s, i) => s + i.seasonEarn, 0);
  const schSeasonEarn = schInstructors.reduce((s, i) => s + i.seasonEarn, 0);
  const totalRevenue = bookings.filter(b => b.status !== 'canceled').reduce((s, b) => s + (b.priceSnapshot || 0), 0);
  // Pass 47C: unpaid needs POSITIVE evidence — missing / legacy hardcoded 'unpaid' ≠ debt.
  const unpaidCount = bookings.filter(isPositivelyUnpaid).length;

  // Monthly aggregates
  const monthlyCol = {};
  const monthlySch = {};
  for (const m of MONTHS) {
    monthlyCol[m] = { earn: 0, fd: 0, hd: 0 };
    monthlySch[m] = { hours: 0, lessons: 0, gross: 0 };
  }
  for (const inst of colInstructors) {
    for (const m of MONTHS) {
      monthlyCol[m].earn += inst.months[m].grossEarn;
      monthlyCol[m].fd += inst.months[m].fd;
      monthlyCol[m].hd += inst.months[m].hd;
    }
  }
  for (const inst of schInstructors) {
    for (const m of MONTHS) {
      monthlySch[m].hours += inst.months[m].hours;
      monthlySch[m].lessons += inst.months[m].lessons;
      monthlySch[m].gross += inst.months[m].grossEarn;
    }
  }

  return {
    instructors,
    colInstructors,
    schInstructors,
    seasonStats: {
      totalRevenue,
      colSeasonEarn,
      schSeasonEarn,
      unpaidCount,
      // TODO: WPI calculated at 5% of collective gross — no deduction records exist yet
      estimatedWPI: Math.round(colSeasonEarn * 0.05),
      // TODO: SAS commission not stored — estimate at ~22% of total
      estimatedCommission: Math.round(totalRevenue * 0.22),
    },
    monthlyCol,
    monthlySch,
  };
}

// ── School day rate reference ───────────────────────────────────────────────

export const SCHOOL_DAY_RATES = [
  { level: 'L1', fullDay: 25000, halfDay: 12500, nonTeach: 1000 },
  { level: 'L2', fullDay: 30000, halfDay: 15000, nonTeach: 1000 },
  { level: 'L3', fullDay: 35000, halfDay: 17500, nonTeach: 1000 },
  { level: 'L4 / Full cert', fullDay: 40000, halfDay: 20000, nonTeach: 1000 },
];

// ── Format helper ───────────────────────────────────────────────────────────

export { fmt, MONTHS };
